import Vue from "vue"

import ApiClient from "@/lib/api-client"
import throttle from "lodash/throttle"
import debounce from "lodash/debounce"

import { REASONS, LEGACY_ACCESS } from "@/lib/access-utils"
import { ROOT_TREE_ID } from "@/lib/page-utils"

const PAGE_ACCESS_DELAY = 500

export default {
  namespaced: true,

  state: {
    currentPage: {},
    pageData: {}, // TODO: Remove
    pageAttributeValues: {},
    pageCompletions: {},
    pageSequences: {},
    pageProgress: {},
    pageAccess: {},
    pageTrees: {},
    pageSubscriptions: {}, // Subscriptions from current user
    pageSubscribers: {}, // Count of _other_ subscribers for a page
    pageCanStartViaPack: {},
    bookmarkedPages: {},
    toolbarHiddenPages: {},
    pageAccessQueue: [],

    isFetchingPageData: {}
  },

  actions: {
    completePage({ commit }, pageId) {
      return ApiClient.post(`/pages/${pageId}/complete`).then(res => {
        commit("setPageCompletion", {
          pageId: res.data.page_id,
          complete: true
        })
      })
    },

    uncompletePage({ commit }, pageId) {
      return ApiClient.delete(`/pages/${pageId}/complete`).then(() => {
        commit("setPageCompletion", {
          pageId,
          complete: false
        })
      })
    },

    subscribe({ commit, dispatch }, { pageId, startDate }) {
      return ApiClient.post(`/pages/${pageId}/subscribers`, {
        started_at: startDate
      }).then(res => {
        commit("setPageSubscription", res.data.page_id)
        commit("setPageProgress", {
          pageId: res.data.page_id,
          progress: res.data.progress
        })
        dispatch("handleRefetchAccess", REASONS.START_REQUIRED)

        return res.data
      })
    },

    unsubscribe({ dispatch, commit }, pageId) {
      return ApiClient.delete(`/pages/${pageId}/subscribers`).then(res => {
        dispatch("resetPageSequences", pageId)
        dispatch("handleRefetchAccess", REASONS.IS_SCHEDULED)
        commit("deletePageSubscription", pageId)
        return res.data
      })
    },

    fetchProgress({ commit }, pageId) {
      return ApiClient.get(`/pages/${pageId}/complete`).then(res => {
        commit("setPageCompletion", {
          pageId,
          complete: res.data.is_completed
        })
        return res.data
      })
    },

    resetPageSequences({ commit, state }, pageId) {
      const list = (state.pageSequences[pageId]?.list || []).map(s => ({
        ...s,
        is_completed: false
      }))

      commit("setPageSequences", { pageId, data: { list } })
    },

    togglePageBookmark({ commit }, pageId) {
      return ApiClient.post(`/pages/${pageId}/bookmarks/toggle`).then(res => {
        const isBookmarked = res.data.is_bookmarked
        commit("setPageIsBookmarked", { pageId, isBookmarked })

        return isBookmarked
      })
    },

    fetchPageAccess({ commit }, pageId) {
      return ApiClient.get(`/pages/${pageId}/access`).then(res => {
        Object.keys(res.data.page_access).forEach(pageId => {
          commit("setPageAccess", {
            pageId,
            access: res.data.page_access[pageId]
          })
        })

        return res.data.page_access[pageId]
      })
    },

    fetchPageData({ dispatch, commit, state }, pageId) {
      if (state.isFetchingPageData[pageId]) return

      commit("setFetchingPageData", { pageId, fetching: true })

      return dispatch("fetchPageSubscribers", pageId).finally(() => {
        commit("setFetchingPageData", { pageId, fetching: false })
      })
    },

    // TODO: Rename to fetch page data
    fetchPageSubscribers({ commit }, pageId) {
      return ApiClient.get(`/pages/${pageId}/subscribers`).then(res => {
        commit("setPageSubscribers", {
          pageId: res.data.page_id,
          count: res.data.count,
          last_5: res.data.last_5,
          can_schedule_start: res.data.can_schedule_start
        })

        commit("setPageSequences", {
          pageId: res.data.page_id,
          data: {
            homePage: {
              title: res.data.page_title,
              path: res.data.page_path
            },
            list: res.data.sequence
          }
        })

        commit("setPageProgress", {
          pageId: res.data.page_id,
          progress: res.data.progress
        })

        res.data.sequence.forEach(page => {
          commit("setPageCompletion", {
            pageId: page.page_id,
            complete: page.is_completed
          })
        })

        return res.data
      })
    },

    fetchPageTree({ commit }, rootId) {
      const root = rootId ? rootId : ROOT_TREE_ID

      return ApiClient.get("/pages/tree", {
        params: {
          root: root === ROOT_TREE_ID ? null : root
        }
      }).then(res => {
        commit("setPageTree", { root, tree: res.data })

        res.data.forEach(page => {
          commit("setPageCompletion", {
            pageId: page.page_id,
            complete: page.is_completed
          })

          page.pages.forEach(page => {
            commit("setPageCompletion", {
              pageId: page.page_id,
              complete: page.is_completed
            })
          })
        })

        return res.data
      })
    },

    fetchPagePath(_, pageId) {
      return ApiClient.get(`/pages/${pageId}/path`).then(res => {
        return res.data.path
      })
    },

    handleRefetchAccess({ state, dispatch }, reason) {
      const pagesToRefetch = []

      Object.keys(state.pageAccess).forEach(pageId => {
        const access = state.pageAccess[pageId]

        if (access.data?.reason === reason) {
          pagesToRefetch.push(pageId)

          if (access.inherit && state.pageAccess[access.root_id]) {
            pagesToRefetch.push(access.root_id)
          }
        }
      })

      pagesToRefetch.forEach(pageId => {
        dispatch("addToAccessQueue", pageId)
      })
    },

    addToAccessQueue({ commit, dispatch }, pageId) {
      commit("addToAccessQueue", pageId)

      dispatch("processAccessQueue")
    },

    processAccessQueue: throttle(
      ({ commit, state }) => {
        ApiClient.post("/pages/access", {
          page_ids: state.pageAccessQueue
        }).then(res => {
          const pages = Object.keys(res.data.page_access)

          pages.forEach(pageId => {
            commit("setPageAccess", {
              pageId,
              access: res.data.page_access[pageId]
            })
          })

          commit("clearAccessQueue")
        })
      },
      PAGE_ACCESS_DELAY,
      { leading: false, trailing: true }
    )
  },

  mutations: {
    setPageData(state, { pageId, data }) {
      Vue.set(state.pageData, pageId, data)
    },

    setFetchingPageData(state, { pageId, isFetching = true }) {
      Vue.set(state.isFetchingPageData, pageId, isFetching)
    },

    setCurrentPage(
      state,
      { id, rootId, contentRootId, path, actions, access, type, show_tabbar }
    ) {
      state.currentPage = {
        id,
        rootId,
        contentRootId,
        path,
        actions,
        access,
        type,
        show_tabbar
      }
    },

    setPageAttributeValues(state, { pageId, values }) {
      Vue.set(state.pageAttributeValues, pageId, values)
    },

    setPageCompletion(state, { pageId, complete }) {
      Vue.set(state.pageCompletions, pageId, complete)
    },

    setPageCompletions(state, pageCompletions) {
      state.pageCompletions = pageCompletions
    },

    setPageSubscriptions(state, pageSubscriptions) {
      state.pageSubscriptions = pageSubscriptions
    },

    setPageSubscription(state, pageId) {
      Vue.set(state.pageSubscriptions, pageId, true)
    },

    setPageSubscribers(state, { pageId, count, last_5, can_schedule_start }) {
      Vue.set(state.pageSubscribers, pageId, {
        count,
        last_5,
        can_schedule_start
      })
    },

    setPageSequences(state, { pageId, data }) {
      Vue.set(state.pageSequences, pageId, {
        ...(state.pageSequences[pageId] || {}),
        ...data
      })
    },

    setPageProgress(state, { pageId, progress }) {
      Vue.set(state.pageProgress, pageId, {
        ...(state.pageProgress[pageId] || []),
        ...progress
      })
    },

    setPageAccess(state, { pageId, access }) {
      Vue.set(state.pageAccess, pageId, access)
    },

    setPageAccesses(state, pageAccess) {
      Object.keys(pageAccess).map(pageId => {
        Vue.set(state.pageAccess, pageId, pageAccess[pageId])
      })
    },

    setPageCanStartViaPack(state, { pageId, canStartViaPack }) {
      state.pageCanStartViaPack = { [pageId]: canStartViaPack }
    },

    setBookmarkedPages(state, bookmarkedPages) {
      state.bookmarkedPages = bookmarkedPages
    },

    setPageIsBookmarked(state, { pageId, isBookmarked }) {
      Vue.set(state.bookmarkedPages, pageId, isBookmarked)
    },

    deletePageSubscription(state, pageId) {
      Vue.delete(state.pageSubscriptions, pageId)
    },

    hidePageToolbar(state, { pageId, isHidden }) {
      Vue.set(state.toolbarHiddenPages, pageId, isHidden)
    },

    setSubmissionsCount(state, { pageId, count }) {
      Vue.set(state.submissionsCount, pageId, count)
    },

    setPageTree(state, { root, tree }) {
      Vue.set(state.pageTrees, root, tree)
    },

    addToAccessQueue(state, pageId) {
      state.pageAccessQueue.push(pageId)
    },

    clearAccessQueue: debounce(state => {
      state.pageAccessQueue = state.pageAccessQueue.filter(
        id => !state.pageAccess[id]
      )
    }, PAGE_ACCESS_DELAY)
  },

  getters: {
    isPageComplete(state, _, __, rootGetters) {
      return pageId => {
        if (rootGetters["auth/isAuthenticated"]) {
          return !!state.pageCompletions[pageId]
        }

        return false
      }
    },

    getCurrentPageAccess(state, getters) {
      return state.currentPage
        ? getters.getPageAccess(state.currentPage.id)
        : null
    },

    getPageSequence(state) {
      return pageId => state.pageSequences[pageId] || {}
    },

    getPageProgress(state) {
      return pageId => state.pageProgress[pageId] || {}
    },

    getPageAccess(state, getters, _, rootGetters) {
      return (pageId, parentPageId = null) => {
        if (
          state.pageAccess[pageId] === LEGACY_ACCESS ||
          (!!state.pageAccess[pageId]?.inherit && !!parentPageId)
        ) {
          const pageAccess = state.pageAccess[pageId]
          const parentPageAccess = getters.getPageAccess(parentPageId)

          // Pages in Movement are set to "inherit" by default. To make this work as
          // expected we need to do some special handling on the frontend.
          //
          // For pages that do not inherit, we can rely on the response from the server
          //
          // We need to handle inheriting pages in the following cases
          // 1. require_purchase
          //    1.a. The current page allows browsing, but the member has not purchased
          // 2. require_authentication
          //    2.a. The current page allows browsing, but the member is not authenticated
          //
          // In both cases we only apply the special handling if the parent page allows
          // browsing. If the parent page does not allow browsing, then we can just
          // return the page access as is.
          //
          // We also need to take special care to only apply the special handling for
          // inheriting pages if the given page is a child of the parent page or if it is
          // the root of its own access tree
          if (
            !pageAccess.root_id ||
            pageAccess.root_id === pageId ||
            pageAccess.root_id === parentPageId
          ) {
            // If the page is the root of the access tree, then we can just return
            // the page access

            if (
              parentPageAccess &&
              parentPageAccess.data &&
              parentPageAccess.data.reason === REASONS.ALLOW_BROWSING
            ) {
              // If the parent allows browsing, then we need to do some extra checks
              // for inheriting pages that are rendered on the page
              if (
                parentPageAccess.data.require_purchase &&
                !parentPageAccess.data.has_purchased
              ) {
                // When the member has not purchased on a require_purchase w/ allow_browsing
                // we need to lock all the inheriting pages
                return {
                  ...parentPageAccess,
                  granted: false,
                  data: {
                    ...parentPageAccess.data,
                    reason: REASONS.REQUIRE_PURCHASE
                  }
                }
              } else if (
                parentPageAccess.data.require_authentication &&
                !rootGetters["auth/isAuthenticated"]
              ) {
                // When the user is not logged in on a require_purchase w/ allow_browsing
                // we need to lock all the inheriting pages
                return {
                  ...parentPageAccess,
                  granted: false,
                  data: {
                    ...parentPageAccess.data,
                    reason: REASONS.REQUIRE_AUTHENTICATION
                  }
                }
              } else if (!pageAccess.root_id) {
                // Legacy contents should always inherit their access from the page
                // they are rendered on
                return parentPageAccess
              } else {
                // If it's neither of these cases, we should fall back to the page
                // access rules the server gave us
                return state.pageAccess[pageId]
              }
            } else if (!pageAccess.root_id) {
              // Return the parents page access for legacy content types
              return parentPageAccess
            } else {
              return pageAccess
            }
          } else {
            return pageAccess
          }
        } else {
          return state.pageAccess[pageId]
        }
      }
    },

    hasPageAccess(_, getters) {
      return (pageId, parentPageId = null) => {
        const pageAccess = getters.getPageAccess(pageId, parentPageId)

        return pageAccess ? !!pageAccess.granted : undefined
      }
    },

    hasFetchedAccess(_, getters) {
      return pageId => getters.hasPageAccess(pageId) !== undefined
    },

    getCanStartViaPack(state) {
      return pageId => !!state.pageCanStartViaPack[pageId]
    },

    isSubscribedToPage(state) {
      return pageId => !!state.pageSubscriptions[pageId]
    },

    getPageSubscribers(state) {
      return pageId => state.pageSubscribers[pageId]
    },

    isPageBookmarked(state) {
      return pageId => !!state.bookmarkedPages[pageId]
    },

    isToolbarHidden(state) {
      return pageId => !!state.toolbarHiddenPages[pageId]
    },

    submissionsCountByPage(state) {
      return pageId => state.submissionsCount[pageId]
    },

    getPageAttributeValues(state) {
      return pageId => state.pageAttributeValues[pageId]
    },

    getPageAttributeValue(state) {
      return attribute =>
        (state.pageAttributeValues[attribute.page_id] || {})[attribute.slug]
    },

    getPageTree(state) {
      return rootId => {
        const root = rootId ? rootId : ROOT_TREE_ID
        return state.pageTrees[root]
      }
    }
  }
}
